import React, { FC, useCallback, useEffect, useState, MouseEvent } from 'react';
import { useHistory } from 'react-router';
import { ITeacherAssignment } from '../../../../../config/api/models/Assignment';
import { Typography, styled, Theme } from '@material-ui/core';
import { useI18nPath } from '../../../../../config/clientRoutes/hooks';
import {
  ChapterTitle,
  ConfirmationDialog,
  Deadline,
  TaskTypeList,
} from '../../../../shared/components';
import AssignmentWrapper from './AssignmentItemWrapper';
import BookTitle from './BookTitle';
import TrajectoryWrapper from './TrajectoryWrapper';
import { useTranslation } from 'react-i18next';
import { useFetch } from '../../../../shared/hooks';
import apiRoutes from '../../../../../config/api/routes';
import {
  FontAwesomeSpriteButton,
  FontAwesomeSpriteIcon,
} from 'src/modules/shared/components/FontAwesomeLocal';
import { SpriteIconsLight } from 'src/config/api/models/FontAwesome';

type AssignmentItemProps = {
  assignment: ITeacherAssignment;
  onRemove: () => void;
};

const NavigationButton = styled(FontAwesomeSpriteButton)({
  alignSelf: 'center',
});

const AssigneeSubTitle = styled(Typography)<Theme>(({ theme }) => ({
  display: 'block',
  fontSize: theme.typography.h4.fontSize,
  color: theme.palette.secondary.main,
}));

const StyledChapterTitle = styled(ChapterTitle)<Theme>(({ theme }) => ({
  position: 'relative',
  paddingLeft: theme.spacing(3),
  marginLeft: 0,
  display: 'block',
  fontWeight: 700,
}));

const StyledIcon = styled(FontAwesomeSpriteIcon)({
  position: 'absolute',
  left: 0,
});

const StyledDeadline = styled(Deadline)({
  marginBottom: '1rem',
});

const ButtonWrapper = styled('div')({
  display: 'flex',
  justifyContent: 'flex-end',
});

const ContentWrapper = styled('div')({
  position: 'relative',
  paddingLeft: '3rem',
  marginLeft: 0,
  display: 'block',
});

const AssignmentItem: FC<AssignmentItemProps> = ({ assignment, onRemove }) => {
  const { t } = useTranslation();
  const { getPath } = useI18nPath();
  const history = useHistory();
  const [openConfirmDelete, setOpenConfirmDelete] = useState(false);
  const [deleteAssignmentResponse, deleteAssignment] = useFetch(
    apiRoutes.deleteAssignment(assignment.id),
    {
      method: 'DELETE',
    }
  );

  const handleNavigateToTrajectory = useCallback(() => {
    history.push(
      getPath('trajectoryView', {
        bookId: assignment.bookId,
        chapterId: assignment.chapterId,
        trajectoryId: assignment.trajectoryId,
      })
    );
  }, [assignment.bookId, assignment.chapterId, getPath, history, assignment.trajectoryId]);

  const handleRemove = (e: MouseEvent) => {
    e.stopPropagation();
    setOpenConfirmDelete(true);
  };
  const handleCloseConfirm = (result: boolean) => {
    setOpenConfirmDelete(false);
    if (result) deleteAssignment();
  };
  useEffect(() => {
    if (deleteAssignmentResponse.success) {
      onRemove();
    }
  }, [deleteAssignmentResponse, onRemove]);

  return (
    <>
      <ConfirmationDialog
        open={openConfirmDelete}
        title={t('assignments.confirmDelete.title')}
        description={t('assignments.confirmDelete.description').replace(
          '%title%',
          assignment.trajectoryTitle
        )}
        onClose={handleCloseConfirm}
      />
      <AssignmentWrapper>
        <BookTitle>{assignment.bookTitle}</BookTitle>
        <TrajectoryWrapper>
          <StyledChapterTitle variant="body1">{assignment.chapterTitle}</StyledChapterTitle>
          <ContentWrapper>
            <StyledIcon icon={SpriteIconsLight.TRAJECTORY} />
            {assignment.trajectoryTitle}
            <AssigneeSubTitle>
              {assignment.groupIsDefault ? t('groups.default_group') : assignment.groupName}
            </AssigneeSubTitle>
            <TaskTypeList taskTypes={assignment.taskTypes} series={assignment.series} />
          </ContentWrapper>
        </TrajectoryWrapper>
        <div>
          {assignment.expiresAt && <StyledDeadline date={assignment.expiresAt} />}
          <ButtonWrapper>
            <NavigationButton icon={SpriteIconsLight.DELETE} onClick={handleRemove} />
            <NavigationButton
              icon={SpriteIconsLight.ANGLE_RIGHT}
              onClick={handleNavigateToTrajectory}
            />
          </ButtonWrapper>
        </div>
      </AssignmentWrapper>
    </>
  );
};

export default AssignmentItem;
